import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import type { ReservationSetting, ThemeSettings } from '../../../core/models/data';
import { functions } from '../../../core/firebase/config';
import {
  buildReservationTheme,
  defaultThemeSettings,
  type ReservationThemeTokens,
} from '../../../core/ui/reservationTheme';
import type {
  GetThemeStudioActiveThemeRequest,
  GetThemeStudioActiveThemeResponse,
  ThemeStudioActiveTheme,
} from './themeStudioActiveThemeRuntimeContract';
import { normalizeThemeStudioActiveAssetSnapshot, type ThemeStudioActiveAssetSnapshot } from './themePreview/themeStudioAssetContract.ts';

export type { ThemeStudioActiveTheme };

export type ThemeStudioActiveThemeRuntimeSource = 'legacy' | 'active_theme';
export type ThemeStudioActiveThemeRuntimeFallbackReason =
  | 'none'
  | 'missing_active_theme'
  | 'missing_published_state'
  | 'invalid_snapshot'
  | 'mapper_exception';

export interface ThemeStudioActiveThemeRuntimeDiagnostics {
  source: ThemeStudioActiveThemeRuntimeSource;
  fallbackReason: ThemeStudioActiveThemeRuntimeFallbackReason;
  mappedTokens: string[];
  unsupportedTokens: string[];
  invalidTokens: string[];
}

export interface ThemeStudioRuntimeThemeResult {
  source: ThemeStudioActiveThemeRuntimeSource;
  theme: ReservationThemeTokens;
  diagnostics: ThemeStudioActiveThemeRuntimeDiagnostics;
}

export interface ThemeStudioActiveThemeCallableFactory {
  <Request, Response>(name: 'themeStudioGetActiveTheme'): HttpsCallable<Request, Response>;
}

const callableFactory: ThemeStudioActiveThemeCallableFactory = <Request, Response>(name: 'themeStudioGetActiveTheme') =>
  httpsCallable<Request, Response>(functions, name);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const allowedTokenIds = new Set([
  'primary',
  'accent',
  'surface',
  'background',
  'success',
  'danger',
  'highlight',
  'radius',
  'elevation',
  'typographyScale',
  'fontFamily',
  'cardSpacing',
  'sectionSpacing',
]);
const allowedLayoutGroups = new Set(['density', 'hero', 'logoPlacement']);
const allowedTypographyGroups = new Set(['fontFamily', 'scale']);
const allowedComponentSlots = new Set(['card', 'primaryButton', 'secondaryButton', 'input', 'stepper', 'successState']);
const colorTokenIds = new Set(['primary', 'accent', 'surface', 'background', 'success', 'danger', 'highlight']);
const tokenValueAllowlist: Record<string, readonly string[]> = {
  radius: ['sm', 'md', 'lg', 'xl'],
  elevation: ['none', 'low', 'medium', 'mid', 'high'],
  typographyScale: ['S', 'M', 'L'],
};
const layoutValueAllowlist: Record<string, readonly string[]> = {
  density: ['compact', 'balanced', 'spacious'],
  hero: ['hero_small', 'hero_medium', 'hero_large'],
  logoPlacement: ['logo_left', 'logo_center', 'logo_top'],
};
const typographyValueAllowlist: Record<string, readonly string[]> = {
  scale: ['S', 'M', 'L'],
};
const supportedRuntimeTokenIds = new Set([
  'primary',
  'accent',
  'surface',
  'background',
  'success',
  'danger',
  'highlight',
  'radius',
  'elevation',
  'typographyScale',
  'density',
]);
const safeValue = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= 120 &&
  !/[;<>]/.test(value) &&
  !/(javascript:|url\(|expression\(|var\(|calc\(|color-mix\(|oklch\(|lab\()/i.test(value);

const isHexColor = (value: string) => /^#[0-9a-f]{6}$/i.test(value.trim());

const validBucket = (
  bucket: unknown,
  allowedKeys: Set<string>,
  valueAllowlist: Record<string, readonly string[]>,
  invalidTokens: string[],
): bucket is Record<string, string> => {
  if (!isRecord(bucket)) return false;
  let valid = true;
  Object.entries(bucket).forEach(([key, value]) => {
    if (!allowedKeys.has(key) || !safeValue(value)) {
      invalidTokens.push(key);
      valid = false;
      return;
    }
    if (colorTokenIds.has(key) && !isHexColor(value)) {
      invalidTokens.push(key);
      valid = false;
      return;
    }
    const allowedValues = valueAllowlist[key];
    if (allowedValues && !allowedValues.includes(value)) {
      invalidTokens.push(key);
      valid = false;
    }
  });
  return valid;
};

const fallback = (
  legacyTheme: ReservationThemeTokens,
  fallbackReason: ThemeStudioActiveThemeRuntimeFallbackReason,
  invalidTokens: string[] = [],
): ThemeStudioRuntimeThemeResult => ({
  source: 'legacy',
  theme: legacyTheme,
  diagnostics: {
    source: 'legacy',
    fallbackReason,
    mappedTokens: [],
    unsupportedTokens: [],
    invalidTokens: [...new Set(invalidTokens)],
  },
});

const coerceThemeStudioPublishedState = (activeTheme: ThemeStudioActiveTheme | null | undefined) => {
  const invalidTokens: string[] = [];
  if (!activeTheme) return { state: null, fallbackReason: 'missing_active_theme' as const, invalidTokens };
  const state = activeTheme?.publishedState;
  if (!isRecord(state)) return { state: null, fallbackReason: 'missing_published_state' as const, invalidTokens };
  const draftTokens = state.draftTokens;
  const draftLayoutVariants = state.draftLayoutVariants;
  const draftTypographyVariants = state.draftTypographyVariants;
  const draftComponentVariants = state.draftComponentVariants;
  if (!validBucket(draftTokens, allowedTokenIds, tokenValueAllowlist, invalidTokens)) return { state: null, fallbackReason: 'invalid_snapshot' as const, invalidTokens };
  if (!validBucket(draftLayoutVariants, allowedLayoutGroups, layoutValueAllowlist, invalidTokens)) return { state: null, fallbackReason: 'invalid_snapshot' as const, invalidTokens };
  if (!validBucket(draftTypographyVariants, allowedTypographyGroups, typographyValueAllowlist, invalidTokens)) return { state: null, fallbackReason: 'invalid_snapshot' as const, invalidTokens };
  if (!validBucket(draftComponentVariants, allowedComponentSlots, {}, invalidTokens)) return { state: null, fallbackReason: 'invalid_snapshot' as const, invalidTokens };
  return { state: { draftTokens, draftLayoutVariants, draftTypographyVariants, draftComponentVariants }, fallbackReason: 'none' as const, invalidTokens };
};

const mapDensityToUiTheme = (density: string | undefined): ReservationSetting['uiTheme'] | undefined => {
  if (density === 'compact') return 'smooth_touch';
  if (density === 'spacious') return 'classic_elegant';
  return undefined;
};

const resolveActiveAssetSnapshot = (activeTheme: ThemeStudioActiveTheme | null | undefined): ThemeStudioActiveAssetSnapshot | null => {
  return normalizeThemeStudioActiveAssetSnapshot(activeTheme?.activeAssetSnapshot);
};

const applyThemeStudioActiveAssetsToThemeSettings = (
  themeSettings: ThemeSettings,
  activeAssetSnapshot: ThemeStudioActiveAssetSnapshot | null,
): ThemeSettings => {
  if (!activeAssetSnapshot) return themeSettings;
  const backgroundImage = activeAssetSnapshot.background.backgroundImage;
  const headerLogo = activeAssetSnapshot.brandHeader.headerLogo;
  const headerBrandMode = activeAssetSnapshot.brandHeader.headerBrandMode;
  const headerLogoMode = activeAssetSnapshot.brandHeader.headerLogoMode;
  const timeWindowFallbackLogo = activeAssetSnapshot.brandHeader.timeWindowFallbackLogo;

  return {
    ...themeSettings,
    ...(backgroundImage?.source === 'themeStudio' && backgroundImage.url
      ? { backgroundImageUrl: backgroundImage.url }
      : {}),
    ...(headerBrandMode?.mode === 'text' || headerBrandMode?.mode === 'logo'
      ? { headerBrandMode: headerBrandMode.mode }
      : {}),
    ...(headerLogoMode?.mode === 'unitLogo'
      ? { headerLogoMode: 'unit' as const, headerLogoUrl: undefined }
      : headerLogoMode?.mode === 'customLogo'
        ? { headerLogoMode: 'custom' as const }
        : headerLogoMode?.mode === 'none'
          ? { headerLogoMode: 'none' as const, headerLogoUrl: undefined }
          : {}),
    ...(headerLogo?.source === 'themeStudio' && headerLogo.url
      ? { headerLogoUrl: headerLogo.url }
      : {}),
    ...(timeWindowFallbackLogo?.source === 'legacy' && timeWindowFallbackLogo.url
      ? { timeWindowLogoUrl: timeWindowFallbackLogo.url }
      : {}),
  };
};

export const resolveThemeStudioRuntimeTheme = (input: {
  activeTheme?: ThemeStudioActiveTheme | null;
  legacyTheme: ReservationThemeTokens;
  legacyThemeSettings?: ThemeSettings | null;
  legacyUiTheme?: ReservationSetting['uiTheme'];
  runtimeThemeBuilder?: typeof buildReservationTheme;
}): ThemeStudioRuntimeThemeResult => {
  const validation = coerceThemeStudioPublishedState(input.activeTheme);
  const publishedState = validation.state;
  if (!publishedState) return fallback(input.legacyTheme, validation.fallbackReason, validation.invalidTokens);

  const tokens = publishedState.draftTokens;
  const runtimeTokens = [
    ...Object.keys(tokens),
    ...Object.keys(publishedState.draftLayoutVariants),
    ...Object.keys(publishedState.draftTypographyVariants),
    ...Object.keys(publishedState.draftComponentVariants),
  ];
  const mappedTokens = runtimeTokens.filter(token => supportedRuntimeTokenIds.has(token));
  const unsupportedTokens = runtimeTokens.filter(token => !supportedRuntimeTokenIds.has(token));
  const activeAssetSnapshot = resolveActiveAssetSnapshot(input.activeTheme);
  const themeSettings: ThemeSettings = applyThemeStudioActiveAssetsToThemeSettings({
    ...defaultThemeSettings,
    ...(input.legacyThemeSettings || {}),
    ...(tokens.primary ? { primary: tokens.primary } : {}),
    ...(tokens.accent ? { accent: tokens.accent } : {}),
    ...(tokens.surface ? { surface: tokens.surface } : {}),
    ...(tokens.background ? { background: tokens.background } : {}),
    ...(tokens.success ? { success: tokens.success } : {}),
    ...(tokens.danger ? { danger: tokens.danger } : {}),
    ...(tokens.highlight ? { highlight: tokens.highlight } : {}),
    ...(tokens.radius ? { radius: tokens.radius as ThemeSettings['radius'] } : {}),
    ...(tokens.elevation ? { elevation: tokens.elevation as ThemeSettings['elevation'] } : {}),
    ...(tokens.typographyScale ? { typographyScale: tokens.typographyScale as ThemeSettings['typographyScale'] } : {}),
  }, activeAssetSnapshot);
  const uiTheme = mapDensityToUiTheme(publishedState.draftLayoutVariants.density) || input.legacyUiTheme;

  try {
    const builder = input.runtimeThemeBuilder || buildReservationTheme;
    return {
      source: 'active_theme',
      theme: builder(themeSettings, uiTheme),
      diagnostics: {
        source: 'active_theme',
        fallbackReason: 'none',
        mappedTokens,
        unsupportedTokens,
        invalidTokens: [],
      },
    };
  } catch (error) {
    console.warn('Theme Studio active theme runtime mapping failed; falling back to legacy theme.', error);
    return fallback(input.legacyTheme, 'mapper_exception');
  }
};

export const loadThemeStudioActiveTheme = async (
  unitId: string,
  factory: ThemeStudioActiveThemeCallableFactory = callableFactory,
): Promise<ThemeStudioActiveTheme | null> => {
  if (!unitId.trim()) return null;
  try {
    const callable = factory<GetThemeStudioActiveThemeRequest, GetThemeStudioActiveThemeResponse>('themeStudioGetActiveTheme');
    const result = await callable({ unitId });
    return result.data.activeTheme ?? null;
  } catch (error) {
    console.warn('Theme Studio active theme read failed; using legacy runtime theme.', error);
    return null;
  }
};
